import type { NotificationHandle, NotificationOptions } from 'element-plus';
import { ElNotification } from 'element-plus';
import { h } from 'vue';
import { NamedValue } from 'vue-i18n';

import { MaybeArray } from '@/_redesign/shared/types/MaybeArray';
import Button from '@/_redesign/shared/ui/Button/Button.vue';
import { useNotificationVisibility } from '@/composables/useNotificationVisibility';
import { i18n } from '@/i18n';
import { IApiResponse } from '@/types/interfaces/ApiResponse.interface';
import { IServerError } from '@/types/interfaces/ServerError.interface';

const DEFAULT_DURATION = 4500;
const NOTIFICATION_CONFIG: Partial<NotificationOptions> = {
  position: 'top-right',
  customClass: 'v-notification',
  showClose: true,
};

export enum MessageType {
  Pending = '',
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
  Info = 'info',
}

export interface WithParams<T> {
  data: T;
  params?: NamedValue | unknown[];
}

export type BaseNotificationContent = string | WithParams<string>;
export type NotificationContent = string | IServerError | WithParams<string | IServerError>;

export type MaybeWithTitle<T> = T | {
  message: T;
  title: BaseNotificationContent;
};

export interface NotificationAction {
  btnText: string;
  onClick: () => void;
}

export interface NotificationCustomOptions extends Omit<Partial<NotificationOptions>, 'message' | 'title'> {
  type: MessageType;
  message: MaybeArray<NotificationContent>;
  title?: BaseNotificationContent;
  iconClass?: string;
  action?: NotificationAction;
}

function isServerError(content: NotificationContent): content is IServerError {
  return typeof content === 'object' && 'code' in content;
}

function translateContent(content: NotificationContent): string {
  if (typeof content === 'string') {
    return i18n.global.t(content);
  }

  if (isServerError(content)) {
    return i18n.global.t(content.code, content.params ?? []);
  }

  if (!('data' in content)) {
    return i18n.global.t('entity.error.UNKNOWN');
  }

  const stringToTranslate = typeof content.data === 'string'
    ? content.data
    : content.data.code;
  const params = content.params as NamedValue ?? {};

  return i18n.global.t(stringToTranslate, params);
}

function translateMessage(message: MaybeArray<NotificationContent>): string {
  if (Array.isArray(message)) {
    return message
      .map(translateContent)
      .join('\n');
  }

  return translateContent(message);
}

export function notificationCustom({
  type,
  title,
  message,
  action,
  duration = DEFAULT_DURATION,
  ...options
}: NotificationCustomOptions): NotificationHandle {
  const tabVisible = document.visibilityState === 'visible';
  const translatedMessage = translateMessage(message);
  const notificationMessage = action
    ? h('div', { class: 'v-notification__action' }, [
      h('div', { class: 'v-notification__message' }, translatedMessage),
      h(Button, {
        label: action.btnText,
        size: 'tiny',
        type: 'secondary',
        onClick: action.onClick,
      }),
    ])
    : translatedMessage;

  const notification = ElNotification({
    ...NOTIFICATION_CONFIG,
    type,
    title: translateContent(title ?? `notification.${type || 'pending'}_title`),
    message: notificationMessage,
    icon: 'hack-for-render-icon',
    duration: tabVisible ? duration : 0,
    ...options,
  });

  if (!tabVisible) {
    useNotificationVisibility(notification, duration);
  }

  return notification;
}

function separateData(data: MaybeWithTitle<MaybeArray<NotificationContent>>): {
  title?: BaseNotificationContent;
  message: MaybeArray<NotificationContent>;
} {
  if (typeof data === 'object' && 'title' in data && 'message' in data) {
    return data;
  }
  return { message: data };
}

function createNotificationFunction<T extends MessageType.Error>(
  type: T,
): (
  data: MaybeWithTitle<MaybeArray<NotificationContent>>,
  duration?: number,
  action?: NotificationAction
) => NotificationHandle;

function createNotificationFunction<T extends Exclude<MessageType, MessageType.Error>>(
  type: T,
): (
  data: MaybeWithTitle<MaybeArray<BaseNotificationContent>>,
  duration?: number,
  action?: NotificationAction
) => NotificationHandle;

function createNotificationFunction<T extends Exclude<MessageType, MessageType.Error>>(
  type: T,
  defaultMessage: string,
): (
  data?: MaybeWithTitle<MaybeArray<BaseNotificationContent>>,
  duration?: number,
  action?: NotificationAction
) => NotificationHandle;

function createNotificationFunction<
  T extends MessageType,
  D extends MaybeWithTitle<MaybeArray<NotificationContent>>,
>(
  type: T,
  defaultMessage?: string,
) {
  return function typedNotification(
    data: D,
    duration: number = DEFAULT_DURATION,
    action?: NotificationAction,
  ): NotificationHandle {
    const { title, message } = separateData(data);

    return notificationCustom({
      type,
      duration,
      title,
      action,
      message: defaultMessage && !message
        ? defaultMessage
        : message,
    });
  };
}

export const pendingNotification = createNotificationFunction(MessageType.Pending);
export const successNotification = createNotificationFunction(MessageType.Success, 'notification.success');
export const warningNotification = createNotificationFunction(MessageType.Warning);
export const errorNotification = createNotificationFunction(MessageType.Error);
export const infoNotification = createNotificationFunction(MessageType.Info);

export function notificationOnResponse(
  response: IApiResponse<any>,
  messages: { success?: string; error?: string } = {},
) {
  if (response.error) {
    return errorNotification(messages.error || response.error);
  }
  return successNotification(messages.success);
}
